import { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ChevronRight, Loader2, Pause, Play, RefreshCw } from 'lucide-react';

import {
  fetchAllLifecycleEnrollments,
  useLifecycleCampaignDetail,
  useLifecycleCampaigns,
  useLifecycleDryRun,
  useLifecycleEnrollments,
  useLifecycleEvents,
  useLifecycleTestEnroll,
  useSetLifecycleCampaignState,
  type LifecycleCampaignCard,
  type LifecycleDryRunResult,
  type LifecycleEnrollmentRow,
  type LifecycleEventRow,
  type LifecycleTestEnrollResult,
  type LifecycleVariantSummary,
} from '@/lib/api/lifecycleAdminHooks';
import { cn } from '@/lib/cn';
import { fmtDate, Td, Th } from './referralShared';

/** /admin/lifecycle-emails — lifecycle marketing campaign dashboard.
 *
 * Read-only KPIs over the durable Supabase ledger plus the one write that
 * launch needs: the per-campaign on/off switch (lifecycle_campaign_state).
 * The switch stops/starts ENROLLMENT sweeps; delivery of already-triggered
 * AC sequences is AC's (deliberately) — exits handle mid-drip stops.
 */

const pct = (v: number) => `${(v * 100).toFixed(1)}%`;

export function LifecycleEmailsScreen() {
  const { data, isLoading, isError, refetch, isFetching } = useLifecycleCampaigns();

  return (
    <div className="flex flex-col gap-20 p-20">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="type-title-md">Lifecycle emails</h1>
          <p className="type-body-sm text-ink-muted">
            Backend cohorts + AC delivery. The toggle stops/starts enrollment;
            purchases, cancels and unsubscribes exit patients mid-drip
            automatically.
          </p>
        </div>
        <div className="flex items-center gap-15">
          <Link
            to="/admin/lifecycle-emails/builder"
            className="type-label-sm text-ink-secondary hover:text-ink underline"
          >
            Campaign builder
          </Link>
          <button
            type="button"
            className="inline-flex items-center gap-6 type-label-sm text-ink-secondary hover:text-ink"
            onClick={() => refetch()}
          >
            <RefreshCw size={14} className={cn(isFetching && 'animate-spin')} />
            Refresh
          </button>
        </div>
      </div>

      {data && !data.campaigns.some((c) => c.global_flag) && (
        <div className="border border-line-subtle bg-sunken px-15 py-10 type-body-sm text-ink-secondary">
          Global kill switch is OFF (`LIFECYCLE_MARKETING_ENABLED=false`) —
          nothing enrolls or sends regardless of the toggles below.
        </div>
      )}

      {isLoading && <div className="type-body-sm text-ink-muted">Loading…</div>}
      {isError && (
        <div className="type-body-sm text-red-600">Failed to load campaigns.</div>
      )}

      <div className="flex flex-col gap-15">
        {data?.campaigns.map((c) => <CampaignCard key={c.campaign_key} card={c} />)}
      </div>
    </div>
  );
}

function StateToggle({ card }: { card: LifecycleCampaignCard }) {
  const setState = useSetLifecycleCampaignState();
  const on = card.enabled_effective;
  return (
    <button
      type="button"
      disabled={setState.isPending}
      onClick={() => {
        const next = !on;
        const label = next
          ? `LAUNCH ${card.campaign_key}?\n\nEnrollment sweeps run hourly — eligible patients will start receiving real emails.`
          : `Pause ${card.campaign_key}? New enrollments stop immediately.`;
        if (!window.confirm(label)) return;
        setState.mutate({ campaignKey: card.campaign_key, enabled: next });
      }}
      className={cn(
        'inline-flex items-center gap-8 px-12 py-8 type-label-sm border',
        on
          ? 'border-green-700 text-green-800 bg-green-50'
          : 'border-line-subtle text-ink-secondary bg-sunken',
      )}
    >
      {setState.isPending ? (
        <Loader2 size={14} className="animate-spin" />
      ) : on ? (
        <Pause size={14} />
      ) : (
        <Play size={14} />
      )}
      {on ? 'LIVE — pause' : 'Paused — launch'}
    </button>
  );
}

function VariantTable({ variants }: { variants: LifecycleVariantSummary[] }) {
  if (!variants.length) {
    return <div className="type-body-sm text-ink-muted">No enrollments yet.</div>;
  }
  return (
    <table className="w-full text-left">
      <thead>
        <tr>
          <Th>Variant</Th>
          <Th>Enrolled</Th>
          <Th>Sent</Th>
          <Th>Clicked</Th>
          <Th>Add to cart</Th>
          <Th>Purchases</Th>
          <Th>Purchase rate</Th>
          <Th>Cancel 28d</Th>
        </tr>
      </thead>
      <tbody>
        {variants.map((v) => (
          <tr key={v.variant_key} className="border-t border-line-subtle">
            <Td>{v.variant_key}</Td>
            <Td>{v.enrolled}</Td>
            <Td>{v.sent}</Td>
            <Td>{v.email_clicked}</Td>
            <Td>{v.add_to_cart}</Td>
            <Td>{v.purchase_completed}</Td>
            <Td>{pct(v.purchase_rate)}</Td>
            <Td>{pct(v.cancel_requested_28d_rate)}</Td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function CampaignCard({ card }: { card: LifecycleCampaignCard }) {
  const navigate = useNavigate();
  return (
    <div className="border border-line-subtle bg-page">
      <div className="flex items-center justify-between px-15 py-12 border-b border-line-subtle bg-sunken">
        <div className="flex items-center gap-12">
          <span className="type-label-md">{card.campaign_key}</span>
          <span className="type-label-xs text-ink-muted uppercase">{card.kind}</span>
          <span className="type-label-xs text-ink-muted">
            window {card.window_days.min}–{card.window_days.max ?? '∞'}d
            {card.require_renewed_source ? ' · renewed only' : ''}
          </span>
        </div>
        <div className="flex items-center gap-10">
          <StateToggle card={card} />
          <button
            type="button"
            className="inline-flex items-center gap-4 type-label-sm text-ink-secondary hover:text-ink"
            onClick={() =>
              navigate(`/admin/lifecycle-emails/${encodeURIComponent(card.campaign_key)}`)
            }
          >
            Detail <ChevronRight size={14} />
          </button>
        </div>
      </div>
      <div className="px-15 py-12 flex flex-col gap-10">
        <div className="flex gap-20 type-body-sm text-ink-secondary">
          <span>
            Enrolled <strong>{card.enrolled_total}</strong>
            {Object.entries(card.enrolled_by_variant)
              .map(([k, n]) => ` · ${k}: ${n}`)
              .join('')}
          </span>
          <span>Last enrollment {fmtDate(card.last_enrollment_at)}</span>
          <span>
            AC automations {card.automation_ids.length ? card.automation_ids.join(', ') : '—'}
          </span>
          <span>experiment {card.experiment_key ?? '—'}</span>
        </div>
        <VariantTable variants={card.summary?.variants ?? []} />
      </div>
    </div>
  );
}

export function LifecycleCampaignDetailScreen() {
  const { campaignKey } = useParams<{ campaignKey: string }>();
  const { data, isLoading } = useLifecycleCampaignDetail(campaignKey);
  const dryRun = useLifecycleDryRun();
  const [dryRunResult, setDryRunResult] = useState<LifecycleDryRunResult | null>(null);

  if (isLoading || !data) {
    return <div className="p-20 type-body-sm text-ink-muted">Loading…</div>;
  }

  return (
    <div className="flex flex-col gap-20 p-20">
      <div className="flex items-center justify-between">
        <h1 className="type-title-md">{data.campaign_key}</h1>
        <button
          type="button"
          disabled={dryRun.isPending}
          onClick={() =>
            dryRun.mutate(
              { campaignKey: data.campaign_key, limit: 100 },
              { onSuccess: setDryRunResult },
            )
          }
          className="inline-flex items-center gap-8 border border-line-subtle px-12 py-8 type-label-sm hover:bg-sunken"
        >
          {dryRun.isPending && <Loader2 size={14} className="animate-spin" />}
          Dry run — preview cohort
        </button>
      </div>

      <VariantTable variants={data.summary?.variants ?? []} />

      <TestEnroll campaignKey={data.campaign_key} />

      {data.cta_destination && (
        <div className="border border-line-subtle p-15 flex flex-col gap-6">
          <div className="type-label-md">CTA destination</div>
          <code
            className="type-body-xs bg-sunken border border-line-subtle px-10 py-8 select-all break-all cursor-pointer"
            title="Click to copy"
            onClick={() => navigator.clipboard?.writeText(data.cta_destination ?? '')}
          >
            {data.cta_destination}
          </code>
          <p className="type-body-sm text-ink-muted">
            Every email CTA lands here after the click-tracker + magic link.
            PostHog filter: <code className="select-all">{data.cta_posthog_hint}</code>
          </p>
        </div>
      )}

      {dryRunResult && (
        <div className="border border-line-subtle p-15 flex flex-col gap-10">
          <div className="type-label-md">
            Dry run: {dryRunResult.would_enroll_count} would enroll (of{' '}
            {dryRunResult.considered} considered)
          </div>
          <div className="type-body-sm text-ink-secondary">
            Skips:{' '}
            {Object.entries(dryRunResult.skip_reasons)
              .map(([k, n]) => `${k}: ${n}`)
              .join(' · ') || 'none'}
          </div>
          {dryRunResult.would_enroll.length > 0 && (
            <table className="w-full text-left">
              <thead>
                <tr>
                  <Th>Email</Th>
                  <Th>Variant</Th>
                  <Th>Recommendation</Th>
                  <Th>Would send</Th>
                </tr>
              </thead>
              <tbody>
                {dryRunResult.would_enroll.map((d) => (
                  <tr key={d.patient_id} className="border-t border-line-subtle">
                    <Td>{d.email || d.patient_id}</Td>
                    <Td>{d.variant_key ?? '—'}</Td>
                    <Td>{d.recommended_product_id ?? '—'}</Td>
                    <Td>{d.would_send ? 'yes' : 'no (control)'}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <section className="flex flex-col gap-10">
        <div className="flex items-center justify-between">
          <h2 className="type-label-md">Recent enrollments</h2>
          <Link
            to={`/admin/lifecycle-emails/${encodeURIComponent(data.campaign_key)}/enrollments`}
            className="type-label-sm text-ink-secondary hover:text-ink"
          >
            See all →
          </Link>
        </div>
        <EnrollmentTable rows={data.recent_enrollments} />
      </section>

      <EventsSection campaignKey={data.campaign_key} />

      <section className="flex flex-col gap-10">
        <h2 className="type-label-md">Config</h2>
        <pre className="type-body-xs bg-sunken border border-line-subtle p-15 overflow-x-auto">
          {JSON.stringify(data.config, null, 2)}
        </pre>
      </section>
    </div>
  );
}


function TestEnroll({ campaignKey }: { campaignKey: string }) {
  const [email, setEmail] = useState('');
  const [result, setResult] = useState<LifecycleTestEnrollResult | null>(null);
  const testEnroll = useLifecycleTestEnroll();
  return (
    <div className="border border-line-subtle p-15 flex flex-col gap-10">
      <div className="type-label-md">Test enroll</div>
      <p className="type-body-sm text-ink-muted">
        Fires this email through the campaign's REAL ActiveCampaign sequence
        (bypasses eligibility and the launch switch). Patient emails get their
        real referral code / links; unknown emails get sample values. Repeat
        sends re-fire the sequence.
      </p>
      <div className="flex gap-10">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="name@systemlabs.com"
          className="flex-1 border border-line-subtle bg-page px-12 py-8 type-body-sm"
        />
        <button
          type="button"
          disabled={testEnroll.isPending || !email.includes('@')}
          onClick={() => {
            if (!window.confirm(`Send the ${campaignKey} sequence to ${email}?`)) return;
            setResult(null);
            testEnroll.mutate({ campaignKey, email }, { onSuccess: setResult });
          }}
          className="inline-flex items-center gap-8 border border-line-subtle px-12 py-8 type-label-sm hover:bg-sunken disabled:opacity-50"
        >
          {testEnroll.isPending && <Loader2 size={14} className="animate-spin" />}
          Send test
        </button>
      </div>
      {result && (
        <div
          className={cn(
            'type-body-sm',
            result.ok ? 'text-green-800' : 'text-red-600',
          )}
        >
          {result.ok
            ? `Fired (${result.mode}) — trigger ${result.provider_trigger_key ?? '—'}. The email should arrive within a minute.`
            : `Failed: ${result.error ?? result.status ?? 'unknown error'}`}
        </div>
      )}
      {testEnroll.isError && (
        <div className="type-body-sm text-red-600">Request failed.</div>
      )}
    </div>
  );
}


const cTd = 'py-4 pr-12 type-body-xs align-middle';

function EnrollmentTable({ rows }: { rows: LifecycleEnrollmentRow[] }) {
  if (!rows.length) {
    return <div className="type-body-sm text-ink-muted">No enrollments yet.</div>;
  }
  return (
    <table className="w-full text-left">
      <thead>
        <tr className="type-label-xs text-ink-muted">
          <th className="py-4 pr-12 font-normal">Email</th>
          <th className="py-4 pr-12 font-normal">Variant</th>
          <th className="py-4 pr-12 font-normal">Source</th>
          <th className="py-4 pr-12 font-normal">Recommendation</th>
          <th className="py-4 pr-12 font-normal">Status</th>
          <th className="py-4 pr-12 font-normal">Enrolled</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((e) => (
          <tr
            key={e.id}
            className="border-t border-line-subtle"
            title={e.eligibility_reason ? JSON.stringify(e.eligibility_reason) : undefined}
          >
            <td className={cTd}>
              <Link
                to={`/admin/clinical-admin/customers/${encodeURIComponent(e.patient_id)}`}
                className="underline decoration-line-subtle underline-offset-2 hover:text-ink"
              >
                {e.email || e.patient_id}
              </Link>
            </td>
            <td className={cTd}>{e.variant_key ?? '—'}</td>
            <td className={cTd}>{e.source_product_id ?? '—'}</td>
            <td className={cTd}>{e.recommended_product_id ?? '—'}</td>
            <td className={cTd}>{e.status}</td>
            <td className={cTd}>{fmtDate(e.enrolled_at)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function toCsv(rows: LifecycleEnrollmentRow[]): string {
  const cols = [
    'email',
    'patient_id',
    'variant_key',
    'source_product_id',
    'recommended_product_id',
    'status',
    'enrolled_at',
    'eligibility_reason',
  ] as const;
  const esc = (v: unknown) => {
    const str = v == null ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v);
    return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
  };
  return [
    cols.join(','),
    ...rows.map((r) => cols.map((c) => esc(r[c])).join(',')),
  ].join('\n');
}

const ENROLL_PAGE_SIZE = 50;

export function LifecycleEnrollmentsScreen() {
  const { campaignKey } = useParams<{ campaignKey: string }>();
  const [page, setPage] = useState(0);
  const [exporting, setExporting] = useState(false);
  const { data, isLoading } = useLifecycleEnrollments(campaignKey, page, ENROLL_PAGE_SIZE);
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / ENROLL_PAGE_SIZE));

  async function exportCsv() {
    if (!campaignKey || exporting) return;
    setExporting(true);
    try {
      const rows = await fetchAllLifecycleEnrollments(campaignKey);
      const blob = new Blob([toCsv(rows)], { type: 'text/csv' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${campaignKey}-enrollments.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="flex flex-col gap-15 p-20">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="type-title-md">{campaignKey} — enrollments</h1>
          <p className="type-body-sm text-ink-muted">{total} total</p>
        </div>
        <div className="flex items-center gap-10">
          <button
            type="button"
            disabled={exporting || total === 0}
            onClick={exportCsv}
            className="inline-flex items-center gap-8 border border-line-subtle px-12 py-8 type-label-sm hover:bg-sunken disabled:opacity-50"
          >
            {exporting && <Loader2 size={14} className="animate-spin" />}
            Export CSV
          </button>
          <Link
            to={`/admin/lifecycle-emails/${encodeURIComponent(campaignKey ?? '')}`}
            className="type-label-sm text-ink-secondary hover:text-ink"
          >
            ← Back
          </Link>
        </div>
      </div>

      {isLoading ? (
        <div className="type-body-sm text-ink-muted">Loading…</div>
      ) : (
        <EnrollmentTable rows={data?.enrollments ?? []} />
      )}

      {pageCount > 1 && (
        <div className="flex items-center gap-10 type-body-sm">
          <button
            type="button"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="border border-line-subtle px-10 py-5 disabled:opacity-40"
          >
            Prev
          </button>
          <span className="text-ink-muted">
            Page {page + 1} of {pageCount}
          </span>
          <button
            type="button"
            disabled={page + 1 >= pageCount}
            onClick={() => setPage((p) => p + 1)}
            className="border border-line-subtle px-10 py-5 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}


const EVENT_TYPES = [
  '',
  'purchase_completed',
  'add_to_cart',
  'email_clicked',
  'provider_handoff',
  'enrolled',
  'send_skipped',
  'unsubscribe',
  'cancel_requested',
] as const;

const EVENTS_PAGE_SIZE = 25;

function EventsSection({ campaignKey }: { campaignKey: string }) {
  const [eventType, setEventType] = useState<string>('');
  const [page, setPage] = useState(0);
  const { data, isLoading } = useLifecycleEvents(
    campaignKey,
    page,
    eventType || null,
    EVENTS_PAGE_SIZE,
  );
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / EVENTS_PAGE_SIZE));

  return (
    <section className="flex flex-col gap-10">
      <div className="flex items-center justify-between">
        <h2 className="type-label-md">Events</h2>
        <div className="flex items-center gap-10">
          <select
            value={eventType}
            onChange={(e) => {
              setEventType(e.target.value);
              setPage(0);
            }}
            className="border border-line-subtle bg-page px-10 py-6 type-body-sm"
          >
            {EVENT_TYPES.map((t) => (
              <option key={t} value={t}>
                {t === '' ? `All events (${total})` : t}
              </option>
            ))}
          </select>
          <span className="type-body-sm text-ink-muted">{total} matching</span>
        </div>
      </div>
      {isLoading ? (
        <div className="type-body-sm text-ink-muted">Loading…</div>
      ) : (
        <EventsTable rows={data?.events ?? []} />
      )}
      {pageCount > 1 && (
        <div className="flex items-center gap-10 type-body-sm">
          <button
            type="button"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="border border-line-subtle px-10 py-5 disabled:opacity-40"
          >
            Prev
          </button>
          <span className="text-ink-muted">
            Page {page + 1} of {pageCount}
          </span>
          <button
            type="button"
            disabled={page + 1 >= pageCount}
            onClick={() => setPage((p) => p + 1)}
            className="border border-line-subtle px-10 py-5 disabled:opacity-40"
          >
            Next
          </button>
        </div>
      )}
    </section>
  );
}

function EventsTable({ rows }: { rows: LifecycleEventRow[] }) {
  if (!rows.length) {
    return <div className="type-body-sm text-ink-muted">No events.</div>;
  }
  return (
    <table className="w-full text-left">
      <thead>
        <tr className="type-label-xs text-ink-muted">
          <th className="py-4 pr-12 font-normal">Email</th>
          <th className="py-4 pr-12 font-normal">Event</th>
          <th className="py-4 pr-12 font-normal">Message</th>
          <th className="py-4 pr-12 font-normal">Detail</th>
          <th className="py-4 pr-12 font-normal">At</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((ev, i) => (
          <tr
            key={`${ev.enrollment_id}-${ev.event_type}-${ev.created_at}-${i}`}
            className="border-t border-line-subtle"
            title={ev.metadata ? JSON.stringify(ev.metadata) : undefined}
          >
            <td className={cTd}>
              {ev.patient_id ? (
                <Link
                  to={`/admin/clinical-admin/customers/${encodeURIComponent(ev.patient_id)}`}
                  className="underline decoration-line-subtle underline-offset-2 hover:text-ink"
                >
                  {ev.email || ev.patient_id}
                </Link>
              ) : (
                '—'
              )}
            </td>
            <td className={cTd}>{ev.event_type}</td>
            <td className={cTd}>{ev.message_key ?? '—'}</td>
            <td className={cTd}>
              <span className="text-ink-muted">
                {String(
                  (ev.metadata as Record<string, unknown> | null)?.canonical_product_id ??
                    (ev.metadata as Record<string, unknown> | null)?.product_slug ??
                    (ev.metadata as Record<string, unknown> | null)?.source ??
                    '',
                ) || '—'}
              </span>
            </td>
            <td className={cTd}>{fmtDate(ev.created_at)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
